/**
 * Реестр тем для компонентов.
 * Позволяет регистрировать и извлекать HTML/CSS шаблоны по имени компонента и коду темы.
 *
 * Ключ составляется как `${component}-${theme}`, где theme по умолчанию равен `'default'`.
 *
 * Пример:
 *  - 'dialog-default'
 *  - 'dialog-white'
 *  - 'alert-black'
 */
class ThemeRegistry {
    /**
     * Создаёт экземпляр реестра тем.
     *
     * @param mainTheme Название основной темы, используемой по умолчанию вместо 'default' (если указана)
     */
    constructor(mainTheme) {
        /** Внутреннее хранилище тем по ключу `${component}-${theme}` */
        this.themeMap = new Map();
        this.mainTheme = mainTheme;
    }
    /**
     * Регистрирует тему для компонента.
     *
     * @param component Имя компонента (например: 'dialog', 'alert')
     * @param html HTML-шаблон темы
     * @param css CSS-стили темы
     * @param theme Название темы (например: 'white', 'black'). По умолчанию — 'default'.
     */
    registerTheme(component, html, css, theme = 'default') {
        const key = `${component}-${theme}`;
        // Если ничего не передали — предупреждение
        if ((!html || !html.trim()) && (!css || !css.trim())) {
            console.error(`registerTheme: не передан ни HTML, ни CSS для темы "${key}"`);
            return;
        }
        // Получаем предыдущую тему, если уже есть
        const prev = this.themeMap.get(key) ?? { html: '', css: '' };
        const nextHtml = html?.trim() ? html : prev.html;
        const nextCss = css?.trim() ? css : prev.css;
        // Обновляем карту тем
        this.themeMap.set(key, {
            html: nextHtml,
            css: nextCss
        });
    }
    /**
    * Обновляет только HTML-шаблон темы, если она уже зарегистрирована.
    *
    * @param component Имя компонента
    * @param html Новый HTML-шаблон
    * @param theme Название темы (по умолчанию — 'default')
    */
    updateHtml(component, html, theme = 'default') {
        if (!html?.trim()) {
            console.error(`Не удалось обновить HTML: передано пустое значение`);
            return;
        }
        const key = `${component}-${theme}`;
        const existing = this.themeMap.get(key);
        if (!existing) {
            console.error(`Не удалось обновить HTML: тема '${key}' не зарегистрирована`);
            return;
        }
        this.themeMap.set(key, { html, css: existing.css });
    }
    /**
     * Обновляет только CSS стили темы, если она уже зарегистрирована.
     *
     * @param component Имя компонента
     * @param css Новый CSS
     * @param theme Название темы (по умолчанию — 'default')
     */
    updateCss(component, css, theme = 'default') {
        if (!css?.trim()) {
            console.error(`Не удалось обновить CSS: - передано пустое значение`);
            return;
        }
        const key = `${component}-${theme}`;
        const existing = this.themeMap.get(key);
        if (!existing) {
            console.error(`Не удалось обновить CSS: тема '${key}' не зарегистрирована`);
            return;
        }
        this.themeMap.set(key, { html: existing.html, css });
    }
    /**
     * Возвращает зарегистрированную тему для компонента.
     *
     * @param component Имя компонента (например: 'dialog')
     * @param theme Название темы (например: 'white', 'black'). По умолчанию — 'default'.
     * @returns Объект с HTML и CSS или `undefined`, если тема не найдена.
     */
    getTheme(component, theme = 'default') {
        // Для тем по умолчанию проверяем сценарий с измененной темой сайта
        if (theme === 'default' && this.mainTheme && this.mainTheme !== 'default') {
            const mainKey = `${component}-${this.mainTheme}`;
            const mainEntry = this.themeMap.get(mainKey);
            // Если найдена
            if (mainEntry)
                return mainEntry;
        }
        const key = `${component}-${theme}`;
        return this.themeMap.get(key);
    }
}

export { ThemeRegistry };
